/**
 * OIDC `state` (CSRF) issue + validation for the auth callback.
 *
 * PER-FLOW STATE KEYING (fixes issue #287 Symptom 1)
 * --------------------------------------------------
 * The previous design stored the state under ONE origin-wide key
 * (`oidc_state`). That key is a single mutable slot: any second initiation of
 * the login flow — React StrictMode double-invoking the effect, the installed
 * PWA relaunching mid-flow, or a second tab — overwrites it. When the IdP then
 * round-trips back echoing the FIRST flow's state, the single stored slot holds
 * the SECOND flow's value, so the equality check sees present+mismatch and
 * bounces. The bounce lands on /login which silently re-initiates, i.e. the
 * "fails ~2/3 of the time, works on retry" loop.
 *
 * The fix keys each in-flight state by its own value: `oidc_state:<state>`.
 * Concurrent / re-initiated flows no longer clobber each other — each lives
 * under its own key. Validation becomes a MEMBERSHIP test ("did I issue this
 * returned state?") rather than an equality test against a mutable slot.
 *
 * CSRF guarantee is UNCHANGED and still fail-closed: a returned state we never
 * issued has no key in either store -> not valid -> bounce. A forged/replayed
 * state is rejected client-side (defense-in-depth) exactly as before, and the
 * backend remains the authoritative gate (single-use DELETE...RETURNING keyed
 * on the returned state). We only stopped DESTROYING our own legitimate flows.
 *
 * Dual-stored (sessionStorage + localStorage): sessionStorage is the primary,
 * but an installed PWA / standalone context can partition or drop sessionStorage
 * across the cross-host dev -> IdP -> dev redirect, so localStorage is the
 * durable fallback. An absent value on BOTH stores still PROCEEDs (defers to the
 * backend) — real Android Chrome can wipe both across the top-level redirect and
 * bouncing there was the original silent-login bug (#241/#244).
 *
 * Pure (storage injected) so the keying + membership logic is unit-testable.
 */
export interface StateStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  readonly length: number;
  key(index: number): string | null;
}

/** Namespace prefix for per-flow state keys. */
const STATE_PREFIX = 'oidc_state:';

/** How long an issued-but-uncompleted state key is honoured (ms). */
const STATE_TTL_MS = 10 * 60 * 1000; // 10 minutes — an OIDC round-trip is seconds

/** Full storage key for a given state value. */
function stateKey(state: string): string {
  return `${STATE_PREFIX}${state}`;
}

/**
 * Record an issued state under its own per-flow key in BOTH stores.
 * Sweeps expired sibling keys at the same time so abandoned flows (user closed
 * the IdP tab) don't accumulate in localStorage.
 */
export function issueState(state: string, stores: StateStore[], now: number): void {
  for (const store of stores) {
    // Guard per-store: a throwing store (e.g. Safari private-mode QuotaExceeded
    // on setItem) must not prevent the OTHER store from recording the state —
    // localStorage is the durable cross-host fallback, so we want at least one
    // store to hold the key. If BOTH throw, the flow still proceeds and the
    // backend's single-use PKCE check remains the authoritative CSRF gate.
    try {
      sweepExpired(store, now);
      store.setItem(stateKey(state), String(now));
    } catch {
      // ignore this store; try the next
    }
  }
}

/**
 * Was `returnedState` issued by us and still within its TTL?
 *
 * Fail-closed: an empty/absent returned state, or one with no matching key in
 * either store, is NOT valid. This is the client-side CSRF (defense-in-depth)
 * check — the backend independently validates too.
 */
export function isStateIssued(
  returnedState: string | null | undefined,
  stores: StateStore[],
  now: number,
): boolean {
  if (!returnedState) return false;
  const key = stateKey(returnedState);
  for (const store of stores) {
    const issuedAt = store.getItem(key);
    if (issuedAt !== null) {
      const ts = Number(issuedAt);
      // A malformed / non-numeric timestamp is treated as expired (fail-closed).
      if (Number.isFinite(ts) && now - ts <= STATE_TTL_MS) return true;
    }
  }
  return false;
}

/**
 * Did we issue ANY state at all (across either store)? Used to distinguish
 * "storage wiped, nothing to compare -> proceed and defer to backend" from
 * "we have live flows but the returned state isn't one of them -> bounce".
 */
export function hasAnyIssuedState(stores: StateStore[], now: number): boolean {
  for (const store of stores) {
    for (const k of prefixedKeys(store)) {
      const ts = Number(store.getItem(k));
      if (Number.isFinite(ts) && now - ts <= STATE_TTL_MS) return true;
    }
  }
  return false;
}

/**
 * Decide what the callback should do about the client-side state check.
 *
 *  - returned state WAS issued (membership hit)      -> 'proceed' (happy path).
 *  - returned state NOT issued but NO live flows exist -> 'proceed': storage was
 *    wiped across the cross-host redirect (Android/PWA partitioning); nothing to
 *    compare, so defer to the authoritative backend gate. NOT a silent bounce —
 *    a backend rejection surfaces a real error in the callback UI.
 *  - returned state NOT issued but live flows DO exist -> 'bounce': we issued
 *    states and this isn't one of them — a genuine anomaly (forged/replayed/
 *    crossed). Fail closed.
 */
export type StateDecision = 'proceed' | 'bounce';

export function decideStateAction(
  returnedState: string | null | undefined,
  stores: StateStore[],
  now: number,
): StateDecision {
  if (isStateIssued(returnedState, stores, now)) return 'proceed';
  // Not issued (or expired). If we have no live flows at all, storage was wiped
  // -> defer to backend. If we DO have live flows, this state is an anomaly.
  return hasAnyIssuedState(stores, now) ? 'bounce' : 'proceed';
}

/**
 * Remove one completed flow's key from every store (call on success or bounce).
 * When `now` is supplied, also sweeps expired sibling keys so abandoned flows
 * (user closed the IdP tab) don't linger on the bounce path — issueState is the
 * only other sweep site, and a retry loop may never reach it.
 */
export function consumeState(
  state: string | null | undefined,
  stores: StateStore[],
  now?: number,
): void {
  for (const store of stores) {
    if (now !== undefined) sweepExpired(store, now);
    if (state) store.removeItem(stateKey(state));
  }
}

// --- one-shot login restart guard (issue #1469) ------------------------------
//
// When the backend rejects the code exchange with the SDK's state-invalid 400
// ("Invalid or expired state parameter"), AuthCallback restarts login ONCE.
// This marker records that it did, so a second state-invalid 400 within the
// window shows the error instead of restarting again -- the restart can never
// loop. Dual-stored like the state keys: the restart crosses the IdP redirect,
// where sessionStorage can be dropped (Android/PWA), and a guard that vanished
// with it would re-arm the loop. Not prefixed with STATE_PREFIX, so the state
// sweeps never touch it. A malformed value counts as a RECENT retry (fail
// closed: show the error rather than restart).

const LOGIN_RETRY_KEY = 'oidc_login_retry_at';

/** How long a recorded restart suppresses another one (ms). */
const LOGIN_RETRY_WINDOW_MS = STATE_TTL_MS;

/** Record that login was restarted after a state-invalid callback. */
export function markLoginRetry(stores: StateStore[], now: number): void {
  for (const store of stores) {
    try {
      store.setItem(LOGIN_RETRY_KEY, String(now));
    } catch {
      // ignore this store; try the next
    }
  }
}

/** Was login already restarted within the window (in either store)? */
export function hasRecentLoginRetry(stores: StateStore[], now: number): boolean {
  for (const store of stores) {
    let raw: string | null = null;
    try {
      raw = store.getItem(LOGIN_RETRY_KEY);
    } catch {
      continue;
    }
    if (raw === null) continue;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return true; // malformed -> fail closed
    if (now - ts <= LOGIN_RETRY_WINDOW_MS) return true;
  }
  return false;
}

/** Drop the restart marker (on a completed sign-in, or once the error shows). */
export function clearLoginRetry(stores: StateStore[]): void {
  for (const store of stores) {
    try {
      store.removeItem(LOGIN_RETRY_KEY);
    } catch {
      // ignore
    }
  }
}

/** Remove every per-flow state key from every store (hard reset, e.g. on logout). */
export function clearAllStates(stores: StateStore[]): void {
  for (const store of stores) {
    for (const k of prefixedKeys(store)) store.removeItem(k);
  }
}

// --- internals -------------------------------------------------------------

/** All `oidc_state:*` keys currently in a store (snapshotted before mutation). */
function prefixedKeys(store: StateStore): string[] {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k !== null && k.startsWith(STATE_PREFIX)) keys.push(k);
  }
  return keys;
}

/** Drop expired per-flow state keys from a single store. */
function sweepExpired(store: StateStore, now: number): void {
  for (const k of prefixedKeys(store)) {
    const ts = Number(store.getItem(k));
    if (!Number.isFinite(ts) || now - ts > STATE_TTL_MS) store.removeItem(k);
  }
}
